import { UIEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Spinner } from "@twilio-paste/core/spinner";
import { Message } from "@twilio/conversations";
import throttle from "lodash.throttle";

import { MessageBubble } from "./MessageBubble";
import { MessageListScrollToBottom } from "./MessageListScrollToBottom";
import { AppState } from "../store/definitions";
import { getMoreMessages } from "../store/actions/genericActions";
import { getDaysOld } from "../utils/getDaysOld";
import { MessageListSeparator } from "./MessageListSeparator";
import { MESSAGES_SPINNER_BOX_HEIGHT } from "../constants";
import {
    conversationEventContainerStyles,
    conversationEventTitleStyles,
    conversationEventDateStyles,
    spinnerContainerStyles,
    messageListStyles,
    innerContainerStyles,
    outerContainerStyles,
    participantTypingStyles
} from "./styles/MessageList.styles";

const isLastOfUserGroup = (message: Message, i: number, messages: Message[]) => {
    const nextMessage = messages[i + 1];

    // if there's no message afterwards, it's definitely the last of a group
    if (!nextMessage) {
        return true;
    }

    // if the author of the next message is different from current one's, then yes, this message is last of its group
    return nextMessage.author !== message.author;
};

const isFirstOfDateGroup = (message: Message, i: number, messages: Message[]) => {
    const prevMessage = messages[i - 1];

    // if the previous message has a date older than the current message, this message is the first for this date
    return getDaysOld(prevMessage.dateCreated) > getDaysOld(message.dateCreated);
};

export const MessageList = () => {
    const { messages, participants, users, conversation, conversationsClient } = useSelector((state: AppState) => ({
        messages: state.chat.messages,
        participants: state.chat.participants,
        users: state.chat.users,
        conversation: state.chat.conversation,
        conversationsClient: state.chat.conversationsClient
    }));
    const dispatch = useDispatch();
    const messageListRef = useRef<HTMLDivElement>(null);
    const newMessagesSeparatorRef = useRef<HTMLDivElement>(null);
    const messageListBottomRef = useRef<HTMLDivElement>(null);
    const wasAtBottomRef = useRef(true);

    const isLoadingMessages = useRef(false);
    const oldMessagesLength = useRef((messages || []).length);
    const [hasLoadedAllMessages, setHasLoadedAllMessages] = useState(true);
    const [focusIndex, setFocusIndex] = useState(
        messages && messages.length ? messages[messages?.length - 1].index : -1
    );
    const [shouldFocusLatest, setShouldFocusLatest] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    const lastSeenMessageIndex = useRef(messages && messages.length ? messages[messages.length - 1].index : -1);
    const [isDocumentFocused, setIsDocumentFocused] = useState(true);

    const scrollToBottom = (smooth = true) => {
        const el = messageListRef.current;
        if (!el) return;

        el.scrollTo({
            top: el.scrollHeight,
            behavior: smooth ? "smooth" : "auto"
        });

        setShowScrollToBottom(false);
        setUnreadMessagesCount(0);

        if (messages?.length) {
            lastSeenMessageIndex.current = messages[messages.length - 1].index;
        }
    };

    const isBottomInView = () => {
        const container = messageListRef.current;
        const bottomMarker = messageListBottomRef.current;

        if (!container || !bottomMarker) {
            return true; // Default to true if refs aren't ready
        }

        // Get the position of the bottom marker relative to the container
        const containerRect = container.getBoundingClientRect();
        const bottomMarkerRect = bottomMarker.getBoundingClientRect();

        // Check if bottom marker is within the visible area of the container
        return bottomMarkerRect.bottom <= containerRect.bottom && bottomMarkerRect.top >= containerRect.top;
    };

    const updateFocus = (newFocus: number) => {
        if (newFocus < 0 || !messages || !messages.length || newFocus > messages[messages.length - 1].index) {
            return;
        }

        if (shouldFocusLatest) {
            setFocusIndex(messages[messages.length - 1].index);
            setShouldFocusLatest(false);
        } else {
            setFocusIndex(newFocus);
        }
    };

    // const scrollToNewMessagesSeparator = () => {
    //     const container = messageListRef.current;
    //     const separator = newMessagesSeparatorRef.current;

    //     if (!container || !separator) return;

    //     const containerTop = container.getBoundingClientRect().top;
    //     const separatorTop = separator.getBoundingClientRect().top;

    //     const offset = separatorTop - containerTop + container.scrollTop;

    //     container.scrollTo({
    //         top: offset,
    //         behavior: "smooth"
    //     });
    // };

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsDocumentFocused(!document.hidden);
        };

        const handleFocus = () => {
            setIsDocumentFocused(true);
        };

        const handleBlur = () => {
            setIsDocumentFocused(false);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
        };
    }, []);

    const scrollWhenFocused = () => {
        if (wasAtBottomRef.current) {
            scrollToBottom(false);
        } else {
            setShowScrollToBottom(true);
            setUnreadMessagesCount((prev) => prev + 1);
        }

        if (!document.activeElement?.hasAttribute("data-message-bubble")) {
            setShouldFocusLatest(true);
        }
    };
    const scrollWhenNotFocused = () => {
        // Uncomment this after conversation last index not updating on first render issue is fixed in conversations SDK
        // scrollToNewMessagesSeparator();
        setShowScrollToBottom(true);
        setUnreadMessagesCount((prev) => prev + 1);
    };
    useEffect(() => {
        const messageListener = (message: Message) => {
            const belongsToCurrentUser = message.author === conversationsClient?.user.identity;

            if (belongsToCurrentUser) {
                scrollToBottom();
            } else if (isDocumentFocused) {
                scrollWhenFocused();
            } else {
                scrollWhenNotFocused();
            }
        };

        conversation?.addListener("messageAdded", messageListener);

        return () => {
            conversation?.removeListener("messageAdded", messageListener);
        };
    }, [conversation, conversationsClient, isDocumentFocused]);

    const messageLoadingCompleted = () => {
        return messages && oldMessagesLength.current < messages?.length;
    };
    useEffect(() => {
        const checkIfAllMessagesLoaded = async () => {
            try {
                const totalMessagesCount = await conversation?.getMessagesCount();
                if (totalMessagesCount) {
                    setHasLoadedAllMessages(totalMessagesCount === messages?.length);
                }

                if (messageLoadingCompleted()) {
                    isLoadingMessages.current = false;
                    oldMessagesLength.current = messages.length;
                }
            } catch {
                console.log("Something went wrong!");
            }
        };

        checkIfAllMessagesLoaded();
    }, [messages, conversation]);

    const onUserScrollToBottom = () => {
        setShowScrollToBottom(false);
        setUnreadMessagesCount(0);
        if (messages && messages.length) {
            lastSeenMessageIndex.current = messages[messages.length - 1].index;
        }
    };

    const handleScroll = async (event: UIEvent<HTMLDivElement>) => {
        const element = event.target as HTMLDivElement;
        const isAtBottom = isBottomInView();

        wasAtBottomRef.current = isAtBottom;

        if (isAtBottom) {
            // User scrolled to bottom manually
            onUserScrollToBottom();
        } else {
            // User is scrolled up
            setShowScrollToBottom(true);
        }

        const hasReachedTop =
            element.scrollHeight + element.scrollTop - MESSAGES_SPINNER_BOX_HEIGHT <= element.clientHeight;

        // When reaching the top of all messages, load the next chunk
        if (hasReachedTop && conversation && messages && !hasLoadedAllMessages && !isLoadingMessages.current) {
            isLoadingMessages.current = true;
            oldMessagesLength.current = messages.length;
            const totalMessagesCount = await conversation?.getMessagesCount();

            if (totalMessagesCount && messages.length < totalMessagesCount) {
                dispatch(getMoreMessages({ anchor: totalMessagesCount - messages.length - 1, conversation }));
            }
        }
    };

    const renderSeparatorIfApplicable = (message: Message, i: number) => {
        const belongsToCurrentUser = message.author === conversationsClient?.user.identity;
        const isFirstUnreadMessage = message.index === (conversation?.lastReadMessageIndex as number) + 1;

        /*
         * Render date separator above the first message which is the first of a certain date
         * (the first message of any chunk cannot compare itself with previous,
         * and the i = 0 date separator is rendered before "Chat started" section)
         */
        if (i > 0 && isFirstOfDateGroup(message, i, messages as Message[])) {
            return <MessageListSeparator message={message} separatorType="date" />;
        }

        /*
         * Render New separator above the first unread message
         * (messages sent by the current user should be treated as inherently read to avoid flicker)
         */
        if (isFirstUnreadMessage && !belongsToCurrentUser) {
            return <MessageListSeparator message={message} separatorType="new" ref={newMessagesSeparatorRef} />;
        }

        return null;
    };

    const renderChatStarted = () =>
        hasLoadedAllMessages ? (
            <>
                <Box {...conversationEventContainerStyles}>
                    <Text
                        element="CHAT_STARTED__TITLE"
                        as="h3"
                        {...conversationEventTitleStyles}
                        data-test="chat-started"
                    >
                        Chat started
                    </Text>
                    <Text element="CHAT_STARTED__DATE" as="p" {...conversationEventDateStyles}>
                        {conversation?.dateCreated.toLocaleString()}
                    </Text>
                </Box>
            </>
        ) : null;

    const renderChatItems = () => {
        if (!messages) {
            return null;
        }

        /*
         * We use a copy of the messages array where the first message is a placeholder for the loading spinner.
         * By assigning the loading spinner to the same index and key as the previous message,
         * we avoid a snappy scroll position change when loading spinner disappears.
         */
        const spinnerIndex = (messages[0]?.index || 0) - 1;
        const messagesWithSpinner = [
            {
                index: spinnerIndex
            } as Message,
            ...messages
        ];

        return messagesWithSpinner.map((message: Message, i: number) => {
            // First message in array represents the loading spinner
            if (message.index === spinnerIndex) {
                // Only render loading spinner if there are remaining messages to load
                return hasLoadedAllMessages ? null : (
                    <Box {...spinnerContainerStyles} key={message.index}>
                        <Spinner color="colorTextWeak" decorative={false} title="Loading" />
                    </Box>
                );
            }
            // Discount loading spinner from indices
            i -= 1;

            return (
                <Box data-test="all-message-bubbles" key={message.index}>
                    {renderSeparatorIfApplicable(message, i)}
                    <MessageBubble
                        message={message}
                        isLast={i === messages.length - 1}
                        isLastOfUserGroup={isLastOfUserGroup(message, i, messages)}
                        focusable={message.index === focusIndex}
                        updateFocus={updateFocus}
                    />
                </Box>
            );
        });
    };

    const handleFocus = () => {
        // Hand over focus to message bubbles once there is at least one
        if (messages && messages.length && focusIndex < 0) {
            setFocusIndex(messages[messages.length - 1].index);
        }
    };

    return (
        <Box {...messageListStyles} position="relative">
            <Box
                {...outerContainerStyles}
                onScroll={throttle(handleScroll, 1000)}
                ref={messageListRef}
                element="MESSAGE_LIST_SCROLL_CONTAINER"
            >
                <Box
                    element="MESSAGE_LIST_CONTAINER"
                    className="twilio__message-list-container"
                    aria-relevant="additions"
                    {...innerContainerStyles}
                    tabIndex={focusIndex >= 0 ? -1 : 0}
                    onFocus={handleFocus}
                    aria-label="Chat messages"
                    role="application"
                >
                    {renderChatStarted()}
                    {renderChatItems()}
                    <div ref={messageListBottomRef} />
                    {participants
                        ?.filter((p) => p.isTyping && p.identity !== conversationsClient?.user.identity)
                        .map((p) => (
                            <Text {...participantTypingStyles} as="p" key={p.identity}>
                                {users?.find((u) => u.identity === p.identity)?.friendlyName} is typing...
                            </Text>
                        ))}
                </Box>
            </Box>

            <MessageListScrollToBottom
                shouldShow={showScrollToBottom}
                unreadCount={unreadMessagesCount}
                onScrollToBottom={scrollToBottom}
            />
        </Box>
    );
};
